<template>
  <v-container fluid class="background-bg full-height">
    <v-dialog scrollable v-model="showAgreementDialog" width="400">
      <v-card class="pa-4">
        <v-card-text>
          For the purposes of participation in the Open Africa Power project (Project),
          I hereby grant my authorization to the treatment of personal data.
          I acknowledge that the data shared during the application process will be processed for purposes
          closely linked with the management and execution of the Project, or to enable the execution of the
          duties envisaged by the law. Additionally, personal data will be collected and processed
          using automated means and in paper form and will be stored for the entire duration of the
          Project and after its termination for a period not exceeding the terms envisaged by applicable laws.
          I also agree to receive regular communications by Enel Foundation related with the development of
          Open Africa Power and other activities.
        </v-card-text>
        <v-card-actions>
          <v-row justify="end">
            <v-btn color="accent" text @click="disagreeHandler">I Disagree</v-btn>
            <v-btn color="accent" class="ml-4" @click="agreeHandler">I Agree</v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row justify="center" align="center" class="mt-8">
      <v-col sm="6" lg="4" md="4">
        <v-card class="pa-4 d-flex flex-column align-center">
          <div class="mt-2">
            <img class="card-logo" src="../assets/enel_foundation_logo.png" />
          </div>

          <div class="title mt-12 primaryText--text">Sign Up to ENEL</div>

          <v-form
            @submit.prevent="doSignUp"
            class="px-4 mt-4 align-self-stretch d-flex flex-column"
          >
            <v-text-field
              v-model="email"
              name="email"
              label="Email"
              @blur="$v.email.$touch"
              required
              :error-messages="emailRequiredMessage"
            />
            <v-text-field
              flat
              v-model="password"
              :append-icon="showPassword? 'mdi-eye' : 'mdi-eye-off'"
              name="pass"
              :type="showPassword ? 'text' : 'password'"
              label="Password"
              @blur="$v.password.$touch"
              @click:append="showPassword = !showPassword"
              required
              :error-messages="passwordRequired"
            />
            <v-text-field
              v-model="confirmPassword"
              name="pass"
              :type="showPassword ? 'text' : 'password'"
              label="Confirm Password"
              @blur="$v.password.$touch"
              @click:append="showPassword = !showPassword"
              required
              :error-messages="passwordRequired"
            />

            <div class="d-flex align-content-start">
              <v-checkbox v-model="privacyNoteChecked" class="align-start" />

              <div class="caption mt-5">
                I acknowledge that I’ve read the full information on the
                <strong>
                  <a
                    href="https://www.enelfoundation.org/privacy-policy"
                    target="_blank"
                  >privacy note</a>
                </strong>
                and authorize Enel Foundation to use for the purpose of this program my personal data.
              </div>
            </div>

            <div class="d-flex align-content-start mt-2">
              <v-checkbox v-model="communicationsChecked" class="align-start" />

              <div class="caption ml-2 mt-5">
                I agree to receive regular communications by Enel
                Foundation related with the development of Open Africa Power
                and other activities.
              </div>
            </div>

            <transition name="slide-fade">
              <v-alert
                v-if="hasError"
                type="error"
                class="caption pa-1 mt-4"
              >{{ this.errorMessage }}</v-alert>
            </transition>

            <v-btn
              class="mt-12 mx-12"
              :disabled="!canSignUp"
              type="submit"
              color="accent"
              :loading="loading"
            >Sign Up</v-btn>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { required, minLength, email } from "vuelidate/lib/validators";
import { mapActions } from "vuex";
import { mapFields } from "vuex-map-fields";
import { store } from "../store";

export default {
  name: "SignupScreen",
  components: {},
  created() {
    // setTimeout(() => {
    //   this.showAgreementDialog = true;
    // }, 500);
  },
  data() {
    return {
      privacyNoteChecked: false,
      communicationsChecked: false,
      loading: false,
      errorMessage: "",
      email: "",
      password: "",
      acceptAgreements: false,
      confirmPassword: "",
      showAgreementDialog: false,
      showPassword: false
    };
  },
  computed: {
    ...mapFields("beezInCode", ["module"]),
    canSignUp() {
      // user can sign up only if
      // the form is valid and both checkboxes
      // of privacy and communications are checked
      return (
        !this.$v.$invalid &&
        this.privacyNoteChecked &&
        this.communicationsChecked
      );
    },
    emailRequiredMessage() {
      const errors = [];
      if (!this.$v.email.$dirty) return [];
      !this.$v.email.required && errors.push("Email is required.");
      !this.$v.email.email && errors.push("Please enter a valid email address");
      return errors;
    },
    passwordRequired() {
      const errors = [];
      if (!this.$v.password.$dirty) return [];
      !this.$v.password.required && errors.push("Password is required");
      !this.$v.password.minLength &&
        errors.push("Password must be at least 5 characters long");
      return errors;
    },
    hasError() {
      return this.errorMessage && this.errorMessage.length;
    },
    passwordsMatch() {
      return this.password === this.confirmPassword;
    }
  },
  methods: {
    ...mapActions("auth", {
      signUp: "signUp"
    }),
    disagreeHandler() {
      this.showAgreementDialog = false;

      this.$router.replace({
        name: "welcome"
      });
    },
    agreeHandler() {
      this.showAgreementDialog = false;
      this.acceptAgreements = true;
    },
    async doSignUp() {
      if (!this.passwordsMatch) {
        this.errorMessage = "Password does not match the confirm password";
        this.password = "";
        this.confirmPassword = "";
        setTimeout(() => {
          this.errorMessage = "";
        }, 2000);
        return;
      }

      // signup user
      try {
        this.loading = true;

        const user = await this.signUp({
          email: this.email,
          password: this.password,
          accountId: this.module.accountId,
          appSubmissionId: this.module.moduleRefId
        });

        // log event that signup completed

        this.$router.replace({
          name: "verify"
        });
      } catch (error) {
        this.errorMessage = error.message;
        setTimeout(() => {
          this.errorMessage = "";
        }, 3000);
      } finally {
        this.loading = false;
      }
    }
  },
  validations: {
    email: {
      required,
      email
    },
    password: {
      required,
      minLength: minLength(5)
    }
  },

  beforeRouteEnter(to, from, next) {
    
    if (process.env.VUE_APP_DISABLE_ROUTE === "true") {
      next();
      return;
    }

    // check if the account exists
    // if the account does not exists it means
    // that the user did not enter the BeeZ-In Code
    // in this case we will need to redirect him to the
    // welcome screen

    if (!store.state.beezInCode.module.account) {
      next({
        name: "welcome"
      });
    } else {
      next();
    }
  }
};
</script>